import { getTimestamp, owaDate } from 'owa-datetime';

export type MyOutlookBriefingUpdate =
    | { readonly kind: 'current' }
    | { readonly kind: 'available' }
    | { readonly kind: 'refreshing'; readonly baselineId: string | undefined }
    | {
          readonly kind: 'applying';
          readonly source: 'showUpdate';
          readonly baselineId: string | undefined;
      }
    | {
          readonly kind: 'applying';
          readonly source: 'refresh';
          readonly baselineId: string | undefined;
          readonly refreshOutcome: 'pending' | 'failed';
      }
    | { readonly kind: 'failed'; readonly action: 'refresh' | 'showUpdate' };

export type MyOutlookBriefingUpdateEvent =
    | { readonly type: 'newerPublicationObserved' }
    | { readonly type: 'refreshRequested'; readonly displayedId: string | undefined }
    | { readonly type: 'refreshFailed' }
    | { readonly type: 'refreshFinishedWithoutPublication' }
    | { readonly type: 'showUpdateRequested'; readonly displayedId: string | undefined }
    | {
          readonly type: 'briefingReadSettled';
          readonly outcome: 'displayed' | 'failed';
          readonly displayedId: string | undefined;
      };

export const MY_OUTLOOK_BRIEFING_UPDATE_CURRENT: MyOutlookBriefingUpdate = { kind: 'current' };
const AVAILABLE: MyOutlookBriefingUpdate = { kind: 'available' };
const REFRESH_FAILED: MyOutlookBriefingUpdate = { kind: 'failed', action: 'refresh' };
const SHOW_UPDATE_FAILED: MyOutlookBriefingUpdate = { kind: 'failed', action: 'showUpdate' };

export function transitionMyOutlookBriefingUpdate(
    update: MyOutlookBriefingUpdate,
    event: MyOutlookBriefingUpdateEvent
): MyOutlookBriefingUpdate {
    switch (event.type) {
        case 'newerPublicationObserved':
            switch (update.kind) {
                case 'current':
                case 'failed':
                    return AVAILABLE;
                case 'refreshing':
                    return {
                        kind: 'applying',
                        source: 'refresh',
                        baselineId: update.baselineId,
                        refreshOutcome: 'pending',
                    };
                default:
                    return update;
            }
        case 'refreshRequested':
            return update.kind === 'refreshing' || update.kind === 'applying'
                ? update
                : { kind: 'refreshing', baselineId: event.displayedId };
        case 'refreshFailed':
            if (update.kind === 'refreshing') {
                return REFRESH_FAILED;
            }
            if (
                update.kind === 'applying' &&
                update.source === 'refresh' &&
                update.refreshOutcome === 'pending'
            ) {
                return { ...update, refreshOutcome: 'failed' };
            }
            return update;
        case 'refreshFinishedWithoutPublication':
            return update.kind === 'refreshing' ? MY_OUTLOOK_BRIEFING_UPDATE_CURRENT : update;
        case 'showUpdateRequested':
            return update.kind === 'available' ||
                (update.kind === 'failed' && update.action === 'showUpdate')
                ? {
                      kind: 'applying',
                      source: 'showUpdate',
                      baselineId: event.displayedId,
                  }
                : update;
        case 'briefingReadSettled':
            return settleBriefingRead(update, event);
    }
}

function settleBriefingRead(
    update: MyOutlookBriefingUpdate,
    event: Extract<MyOutlookBriefingUpdateEvent, { type: 'briefingReadSettled' }>
): MyOutlookBriefingUpdate {
    if (event.outcome === 'failed') {
        return update.kind === 'applying' ? SHOW_UPDATE_FAILED : update;
    }
    switch (update.kind) {
        // BriefingRead returns the latest publication, so any displayed read supersedes a held update.
        case 'available':
            return MY_OUTLOOK_BRIEFING_UPDATE_CURRENT;
        case 'failed':
            return update.action === 'showUpdate' ? MY_OUTLOOK_BRIEFING_UPDATE_CURRENT : update;
        case 'refreshing':
            return event.displayedId !== update.baselineId
                ? MY_OUTLOOK_BRIEFING_UPDATE_CURRENT
                : update;
        case 'applying':
            if (event.displayedId !== update.baselineId) {
                return MY_OUTLOOK_BRIEFING_UPDATE_CURRENT;
            }
            if (update.source === 'showUpdate') {
                return MY_OUTLOOK_BRIEFING_UPDATE_CURRENT;
            }
            return update.refreshOutcome === 'failed'
                ? REFRESH_FAILED
                : { kind: 'refreshing', baselineId: update.baselineId };
        default:
            return update;
    }
}

export function isAwaitingMyOutlookRefresh(update: MyOutlookBriefingUpdate): boolean {
    return (
        update.kind === 'refreshing' ||
        (update.kind === 'applying' &&
            update.source === 'refresh' &&
            update.refreshOutcome === 'pending')
    );
}

// Ignore an out-of-order StateRead that predates the briefing already on screen.
export function isNewerMyOutlookPublication(
    latest: { readonly publishedId?: string; readonly generatedAt?: string } | undefined,
    displayed: { readonly id: string; readonly generatedAt: string } | null
): boolean {
    if (!latest?.publishedId || !displayed || latest.publishedId === displayed.id) {
        return false;
    }
    const latestTime = toTimestamp(latest.generatedAt);
    const displayedTime = toTimestamp(displayed.generatedAt);
    return latestTime === undefined || displayedTime === undefined || latestTime > displayedTime;
}

function toTimestamp(isoTimestamp: string | undefined): number | undefined {
    if (!isoTimestamp) {
        return undefined;
    }
    const timestamp = getTimestamp(owaDate('UTC', isoTimestamp));
    return Number.isFinite(timestamp) ? timestamp : undefined;
}
